import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import type { ValidIconSymbolName } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';

const PanelWrapper = styled('div', { padding: 15, margin: 4 });
const MessageBlock = styled('div', { padding: 15, margin: 4, marginBottom: 12.5, textAlign: 'center' });
const IconBlock = styled('div', { padding: 15, margin: 4 });
const LinkBlock = styled('div', { padding: 15, margin: 4 });

type ResultProps = {
    messageId: string,  /* used for id attribute in the html */
    message: string,    /* should be localized prior to sending here */
    icon: ValidIconSymbolName,       /* assumes icon is from Abyss icon set */
    iconColor: string,
    linkId: string,     /* used for id attribute in the html */
    linkText: string,   /* should be localized prior to sending here */
    linkTo: string,
    onLinkClick?: (() => void) | (() => Promise<void>)
};

export const ResultPanel = ({
    messageId,
    message,
    icon,
    iconColor,
    linkId,
    linkTo,
    linkText,
    onLinkClick
}: ResultProps) => {
    const navigate = useNavigate();

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!onLinkClick) {
            return;
        }

        // Preserve standard link behavior for modified/middle clicks.
        if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
            return;
        }

        e.preventDefault();
        try {
            await onLinkClick();
        } finally {
            navigate(linkTo);
        }
    };

    return (
        <PanelWrapper>
            <Layout.Stack alignLayout="center">
                <MessageBlock>
                    <Heading id={messageId} level={5} color="black">{message}</Heading>
                </MessageBlock>
                <IconBlock>
                    <IconSymbol icon={icon} size={72} color={iconColor} />
                </IconBlock>
                <LinkBlock>
                    <Link id={linkId} to={linkTo} onClick={handleClick} >{linkText}</Link>
                </LinkBlock>
            </Layout.Stack>
        </PanelWrapper>
    );
}
